import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { DesignProps } from 'api/DesignApi';

export type LifeStoryResponse = {
  id?: string;
  displayText?: string;
  text?: string;
  responseText?: string;
  transcriptText?: string;
  editedTranscriptText?: string;
  audioUrl?: string;
  audioDownloadUrl?: string;
  recordedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type LifeStoryPrompt = {
  id?: string;
  prompt?: string;
  promptText?: string;
  question?: string;
  response?: LifeStoryResponse | null;
  responses?: LifeStoryResponse[];
};

export type LifeStorySection = {
  id?: string;
  key?: string;
  sectionKey?: string;
  title?: string;
  description?: string;
  prompts?: LifeStoryPrompt[];
  responses?: LifeStoryResponse[];
};

export type PublicLifeStory = {
  id?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  designProps?: DesignProps;
  signupUrl?: string;
  tenantOrganizationName?: string;
  tenantOrganizationLogoUrl?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  sections?: LifeStorySection[];
};

export type MyLifeReviewSharePreview = PublicLifeStory | PublicLifeStory[];

type MyLifeReviewSharePreviewResponse = {
  data: MyLifeReviewSharePreview;
};

export type LifeReviewResponse = {
  id?: string;
  promptId?: string;
  promptTemplateId?: string;
  displayText?: string;
  durationSec?: number;
  hasAudio?: boolean;
  transcriptionStatus?: string;
  transcriptionError?: string;
  prompt?: string;
  promptText?: string;
  question?: string;
  text?: string;
  responseText?: string;
  transcriptText?: string;
  editedTranscriptText?: string;
  audioStorageKey?: string;
  audioUrl?: string;
  audioDownloadUrl?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type LifeReviewPrompt = {
  id: string;
  prompt?: string;
  promptText?: string;
  question?: string;
  sectionId?: string;
  sectionKey?: string;
  response?: LifeReviewResponse | null;
  responses?: LifeReviewResponse[];
};

export type LifeReviewPromptSection = {
  id?: string;
  key?: string;
  sectionKey?: string;
  title?: string;
  description?: string;
  prompts?: LifeReviewPrompt[];
};

export type LifeReview = {
  id?: string;
  userId?: string | null;
  patientProfileId?: string | null;
  status?: string;
  progress?: number;
  completionPercent?: number;
  completionPercentage?: number;
  sections?: LifeReviewPromptSection[];
  responses?: LifeReviewResponse[];
};

type LifeReviewEnvelope = {
  data: {
    lifeReview: LifeReview | null;
    progress?: number;
    sections?: LifeReviewPromptSection[];
  };
};

type LifeReviewPromptsEnvelope = {
  data: {
    sections?: LifeReviewPromptSection[];
    prompts?: LifeReviewPrompt[];
  };
};

export type CompleteLifeReviewSessionPayload = {
  responses: Array<{
    promptTemplateId: string;
    responseText?: string;
    audioStorageKey?: string;
  }>;
};

export type UpdateMyLifeReviewResponsePayload = {
  responseId: string;
  editedTranscriptText: string;
};

export type GetLifeReviewAudioUploadUrlPayload = {
  promptTemplateId: string;
  contentType: string;
};

export type CreateMyLifeReviewSharePayload = {
  recipientEmail: string;
  recipientName?: string;
};

export type CreateMyLifeReviewShareResponse = {
  invite?: {
    inviteId?: string;
    recipientName?: string;
    recipientEmail?: string;
    createdAt?: string;
    lastSentAt?: string;
    isActive?: boolean;
  };
  publicUrl?: string;
};

export type MyLifeReviewShareListItemResponse = {
  id: string;
  userId?: string | null;
  patientProfileId?: string | null;
  recipientName?: string;
  recipientEmail?: string;
  status?: 'active' | 'opened' | 'expired' | 'revoked';
  expiresAt?: string;
  createdAt?: string;
  sentAt?: string;
  firstOpenedAt?: string;
  lastOpenedAt?: string;
  openedTimes?: number;
};

type PublicLifeStoryApiResponse = {
  data: PublicLifeStory & {
    patient?: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
  };
};

const asRecord = (value: unknown): Record<string, unknown> => (value && typeof value === 'object' ? (value as Record<string, unknown>) : {});

const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

type RawIdentity = {
  userId?: unknown;
  patientProfileId?: unknown;
  profileId?: unknown;
};

const normalizeIdentity = <T extends object>(
  payload: T
): T & {
  userId: string | null;
  patientProfileId: string | null;
} => {
  const source = payload as T & RawIdentity;

  return {
    ...payload,
    userId: asStringOrNull(source.userId),
    patientProfileId: asStringOrNull(source.patientProfileId) ?? asStringOrNull(source.profileId)
  };
};

const dedupeById = <T extends { id?: string }>(items: T[]): T[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (!item.id) {
      return true;
    }

    if (seen.has(item.id)) {
      return false;
    }

    seen.add(item.id);
    return true;
  });
};

export const normalizePublicLifeStory = (value: unknown): PublicLifeStory => {
  const source = asRecord(value);
  const nestedLifeStory = asRecord(source.lifeStory);
  const lifeStory = Object.keys(nestedLifeStory).length ? nestedLifeStory : source;
  const patient = { ...asRecord(source.patient), ...asRecord(lifeStory.patient) };
  const branding = { ...asRecord(source.branding), ...asRecord(lifeStory.branding) };
  const share = { ...asRecord(source.share), ...asRecord(lifeStory.share) };
  const invite = { ...asRecord(source.invite), ...asRecord(lifeStory.invite) };
  const organization = { ...asRecord(source.organization), ...asRecord(lifeStory.organization) };
  const tenantOrganization = { ...asRecord(source.tenantOrganization), ...asRecord(lifeStory.tenantOrganization) };
  const designProps = {
    ...asRecord(source.designProps),
    ...asRecord(lifeStory.designProps),
    ...asRecord(patient.designProps),
    ...asRecord(branding.designProps),
    ...asRecord(share.designProps),
    ...asRecord(invite.designProps),
    ...asRecord(organization.designProps),
    ...asRecord(tenantOrganization.designProps)
  } as DesignProps;
  const normalizedDesignProps = Object.keys(designProps).length ? designProps : undefined;
  const signupUrl = asStringOrNull(normalizedDesignProps?.domainUrl);
  const firstName = asStringOrNull(lifeStory.firstName) ?? asStringOrNull(source.firstName) ?? asStringOrNull(patient.firstName);
  const lastName = asStringOrNull(lifeStory.lastName) ?? asStringOrNull(source.lastName) ?? asStringOrNull(patient.lastName);
  const fullNameFromParts = [firstName, lastName].filter(Boolean).join(' ').trim();
  const fullName =
    asStringOrNull(lifeStory.fullName) ??
    asStringOrNull(source.fullName) ??
    asStringOrNull(patient.fullName) ??
    (fullNameFromParts || undefined);

  return {
    ...lifeStory,
    designProps: normalizedDesignProps,
    firstName: firstName ?? undefined,
    lastName: lastName ?? undefined,
    fullName,
    signupUrl: signupUrl ?? undefined,
    tenantOrganizationName:
      asStringOrNull(lifeStory.tenantOrganizationName) ??
      asStringOrNull(source.tenantOrganizationName) ??
      asStringOrNull(lifeStory.organizationName) ??
      asStringOrNull(source.organizationName) ??
      asStringOrNull(branding.tenantOrganizationName) ??
      asStringOrNull(branding.organizationName) ??
      asStringOrNull(organization.name) ??
      asStringOrNull(tenantOrganization.name) ??
      asStringOrNull(organization.organizationName) ??
      asStringOrNull(tenantOrganization.organizationName) ??
      asStringOrNull(share.tenantOrganizationName) ??
      asStringOrNull(invite.tenantOrganizationName) ??
      asStringOrNull(normalizedDesignProps?.companyName) ??
      undefined,
    tenantOrganizationLogoUrl:
      asStringOrNull(lifeStory.tenantOrganizationLogoUrl) ??
      asStringOrNull(source.tenantOrganizationLogoUrl) ??
      asStringOrNull(lifeStory.organizationLogoUrl) ??
      asStringOrNull(source.organizationLogoUrl) ??
      asStringOrNull(branding.tenantOrganizationLogoUrl) ??
      asStringOrNull(branding.organizationLogoUrl) ??
      asStringOrNull(organization.logoUrl) ??
      asStringOrNull(tenantOrganization.logoUrl) ??
      asStringOrNull(share.tenantOrganizationLogoUrl) ??
      asStringOrNull(invite.tenantOrganizationLogoUrl) ??
      asStringOrNull(normalizedDesignProps?.logoUrl) ??
      undefined,
    heroTitle: asStringOrNull(lifeStory.heroTitle) ?? asStringOrNull(source.heroTitle) ?? asStringOrNull(branding.heroTitle) ?? undefined,
    heroSubtitle:
      asStringOrNull(lifeStory.heroSubtitle) ?? asStringOrNull(source.heroSubtitle) ?? asStringOrNull(branding.heroSubtitle) ?? undefined
  };
};

export const lifeReviewApi = createApi({
  reducerPath: 'lifeReviewApi',
  baseQuery: baseQueryWithReAuth,
  endpoints: (build) => ({
    getMyLifeReview: build.query<LifeReview | null, void>({
      query: () => ({
        url: '/users/me/life-review'
      }),
      transformResponse: (response: LifeReviewEnvelope) => {
        const lifeReview = response.data.lifeReview;

        if (!lifeReview) {
          return null;
        }

        return {
          ...normalizeIdentity(lifeReview),
          progress: response.data.progress ?? lifeReview.progress,
          sections: response.data.sections || lifeReview.sections || [],
          responses: dedupeById((lifeReview.responses || []).map((item) => normalizeIdentity(item)))
        };
      }
    }),
    getMyLifeReviewPrompts: build.query<LifeReviewPromptSection[], void>({
      query: () => ({
        url: '/users/me/life-review/prompts'
      }),
      transformResponse: (response: LifeReviewPromptsEnvelope) => response.data.sections || []
    }),
    createMyLifeReview: build.mutation<LifeReview, void>({
      query: () => ({
        url: '/users/me/life-review',
        method: 'POST'
      }),
      transformResponse: (response: { data: LifeReview }) => response.data
    }),
    completeMyLifeReviewSession: build.mutation<LifeReview, CompleteLifeReviewSessionPayload>({
      query: (body) => ({
        url: '/users/me/life-review/session/complete',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: LifeReview }) => response.data
    }),
    getMyLifeReviewAudioUploadUrl: build.mutation<
      { uploadUrl: string; audioStorageKey: string },
      GetLifeReviewAudioUploadUrlPayload
    >({
      query: (body) => ({
        url: '/users/me/life-review/audio-upload-url',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { uploadSignedUrl: string; audioStorageKey: string } }) => ({
        uploadUrl: response.data.uploadSignedUrl,
        audioStorageKey: response.data.audioStorageKey
      })
    }),
    getMyLifeReviewResponseAudioDownloadUrl: build.mutation<{ audioDownloadUrl: string }, { responseId: string }>({
      query: ({ responseId }) => ({
        url: `/users/me/life-review/responses/${encodeURIComponent(responseId)}/audio-download-url`,
        method: 'POST'
      }),
      transformResponse: (response: { data: { audioDownloadUrl: string } }) => response.data
    }),
    transcribeMyLifeReviewResponse: build.mutation<{ success: boolean }, { responseId: string }>({
      query: ({ responseId }) => ({
        url: `/users/me/life-review/responses/${encodeURIComponent(responseId)}/transcribe`,
        method: 'POST'
      }),
      transformResponse: (response: { success: boolean }) => ({ success: response.success })
    }),
    deleteMyLifeReviewResponse: build.mutation<{ success: boolean }, { responseId: string }>({
      query: ({ responseId }) => ({
        url: `/users/me/life-review/responses/${encodeURIComponent(responseId)}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { success: boolean }) => ({ success: response.success })
    }),
    updateMyLifeReviewResponse: build.mutation<LifeReviewResponse, UpdateMyLifeReviewResponsePayload>({
      query: ({ responseId, editedTranscriptText }) => ({
        url: `/users/me/life-review/responses/${encodeURIComponent(responseId)}`,
        method: 'PATCH',
        body: {
          editedTranscriptText
        }
      }),
      transformResponse: (response: { data: LifeReviewResponse }) => response.data
    }),
    createMyLifeReviewShare: build.mutation<CreateMyLifeReviewShareResponse, CreateMyLifeReviewSharePayload>({
      query: (body) => ({
        url: '/users/me/life-review/shares',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: CreateMyLifeReviewShareResponse }) => response.data
    }),
    getMyLifeReviewShares: build.query<MyLifeReviewShareListItemResponse[], void>({
      query: () => ({
        url: '/users/me/life-review/shares'
      }),
      transformResponse: (response: { data?: MyLifeReviewShareListItemResponse[] }) =>
        dedupeById((response.data || []).map((item) => normalizeIdentity(item)))
    }),
    resendMyLifeReviewShare: build.mutation<{ success: boolean }, { shareId: string }>({
      query: ({ shareId }) => ({
        url: `/users/me/life-review/shares/${encodeURIComponent(shareId)}/resend`,
        method: 'POST'
      }),
      transformResponse: (response: { success?: boolean }) => ({ success: Boolean(response.success) })
    }),
    deleteMyLifeReviewShare: build.mutation<{ success: boolean }, { shareId: string }>({
      query: ({ shareId }) => ({
        url: `/users/me/life-review/shares/${encodeURIComponent(shareId)}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { success?: boolean }) => ({ success: Boolean(response.success) })
    }),
    getMyLifeReviewSharePreview: build.query<MyLifeReviewSharePreview, void>({
      query: () => ({
        url: '/users/me/life-review/share-preview'
      }),
      transformResponse: (response: MyLifeReviewSharePreviewResponse) => response.data
    }),
    getPublicLifeStory: build.query<PublicLifeStory, { token: string }>({
      query: ({ token }) => ({
        url: `/public/life-story/${encodeURIComponent(token)}`
      }),
      transformResponse: (response: PublicLifeStoryApiResponse) => normalizePublicLifeStory(response.data),
      extraOptions: {
        skipReAuth: true
      }
    })
  })
});

export const {
  useGetMyLifeReviewQuery,
  useGetMyLifeReviewPromptsQuery,
  useCreateMyLifeReviewMutation,
  useCompleteMyLifeReviewSessionMutation,
  useGetMyLifeReviewAudioUploadUrlMutation,
  useGetMyLifeReviewResponseAudioDownloadUrlMutation,
  useTranscribeMyLifeReviewResponseMutation,
  useDeleteMyLifeReviewResponseMutation,
  useUpdateMyLifeReviewResponseMutation,
  useCreateMyLifeReviewShareMutation,
  useGetMyLifeReviewSharesQuery,
  useResendMyLifeReviewShareMutation,
  useDeleteMyLifeReviewShareMutation,
  useGetMyLifeReviewSharePreviewQuery,
  useGetPublicLifeStoryQuery
} = lifeReviewApi;
